/**
 * The frame around signing in and signing up.
 *
 * Split down the middle: a panel in the brand green carrying the one promise the product makes,
 * and beside it the form on white. A form floating in the centre of an empty page says nothing
 * about what it is for, and this is the only screen a stranger sees before deciding whether to
 * type anything.
 *
 * The panel carries the mark, so it is not decorative and is not hidden from assistive
 * technology — only from narrow screens, where the form takes the whole width.
 *
 * The panel is hidden below `lg` — on a phone there is no room for it, and pushing the form under
 * a screenful of marketing is how a sign-in gets abandoned. It is also *ordered after* the form in
 * the markup and placed first with CSS, so a screen reader and a keyboard reach the form first
 * whatever the layout is doing.
 */

import { useSession } from "@/lib/session";
import type { ReactNode } from "react";
import { AuthBackdrop } from "@/components/auth-backdrop";
import { Wordmark } from "@/components/wordmark";
import { Copyright } from "@/components/copyright";
import { RecaptchaNotice } from "@/components/recaptcha";

/** What the right-hand panel says. Kept here so both screens tell the same story. */
const POINTS = [
  {
    title: "Every unit, every payment, every renewal",
    body: "One place for the whole portfolio — who owes what, which tenancies end soon, and what has actually been collected.",
  },
  {
    title: "Nothing found out after the fact",
    body: "Arrears and expiries are chased automatically, on your wording, inside your sending hours.",
  },
  {
    title: "Built for how rent is really paid",
    body: "Annual, quarterly, or any term you agree — one invoice for the period, not twelve pretending to be one.",
  },
];

export function AuthShell({ children }: { children: ReactNode }) {
  /*
    Where the mark goes.

    The public site, which is the only page a person on this screen might want that is not this
    screen. It falls back to the sign-up form rather than to nothing on the rare occasion the
    address has not arrived — a mark that goes somewhere sensible beats one that goes nowhere.
  */
  const { listingsUrl } = useSession();
  const home = listingsUrl || "/sign-up";

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-2">
      {/* The form half. The ground is a light grey so the card itself can be the white thing on
          it — a white card on a white page has to be drawn with a border to exist at all. */}
      <div className="flex min-h-screen flex-col bg-ink-50 lg:min-h-0">
        {/* On narrow screens the green panel is hidden, so the mark has to live here instead —
            otherwise a phone gets a sign-in page with no name on it. */}
        <header className="px-5 py-5 sm:px-8 lg:hidden">
          {/* The mark goes to the public site, not to this page.
              `/` is the sign-in form now, so linking the logo there made it the one control on the
              screen that does nothing when pressed. The site is where somebody who is not signing
              in actually wants to be. */}
          <a href={home} className="inline-flex items-center gap-2.5">
            <img src="/logo.svg" alt="" width={30} height={30} />
            <Wordmark className="text-lg" />
          </a>
        </header>

        <main className="flex flex-1 items-center justify-center px-5 pb-12 sm:px-8">
          {/* 8px here rather than the 12px surfaces use elsewhere: this card is the whole screen,
              and a softer corner at that size reads as a dialog rather than a page. */}
          <div className="auth-card w-full max-w-md">{children}</div>
        </main>

        {/* Under the card rather than inside it: the notice covers the page, not the form.

            The captcha line is here for the same reason and for one more — every screen inside
            this shell is a protected form, so saying it once under the card beats repeating it in
            four places that would then have to be kept in step. It draws nothing when no keys are
            configured. */}
        <footer className="px-5 pb-8 text-center sm:px-8">
          <RecaptchaNotice className="mx-auto mb-3 max-w-md" />
          <Copyright />
        </footer>
      </div>

      {/* The brand half. Decorative, so it is hidden from assistive technology and from phones. */}
      <aside
        className="relative hidden overflow-hidden bg-brand-900 p-12 text-white lg:order-first lg:flex lg:flex-col lg:justify-center"
      >
        <AuthBackdrop />

        <div className="relative max-w-md">
          {/* Same left edge as everything below it: the mark is the first line of the column, not
              a badge floating in the corner. */}
          <a href={home} className="inline-flex items-center gap-3">
            <img src="/logo.svg" alt="" width={48} height={48} />
            <Wordmark tone="light" className="text-2xl" />
          </a>

          <p className="eyebrow mt-12 text-brand-300">Property management</p>
          <h2 className="mt-3 text-3xl font-semibold leading-tight tracking-tight text-white">
            Run the whole portfolio from one screen.
          </h2>

          <ul className="mt-10 space-y-7">
            {POINTS.map((point) => (
              <li key={point.title} className="flex gap-4">
                <span className="mt-1.5 size-2 shrink-0 rounded-full bg-brand-400" />
                <div>
                  <p className="font-semibold text-white">{point.title}</p>
                  <p className="mt-1 text-sm leading-relaxed text-white/70">{point.body}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </aside>
    </div>
  );
}
